import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';
import useFormHook from '@shared/hooks/useFormHook';

export const useUsersByGroup = (onClose, groupId) => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [usersToRemove, setUsersToRemove] = useState([]);
    
    const { modalRef } = useFormHook({}, onClose);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await userGroupsService.getUsersByGroup(groupId);
            setUsers(response);
        } catch (err) {
            setError(t('userGroups.error.loading'));
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, [groupId, t]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleRemoveUser = useCallback((userId) => {
        setUsersToRemove(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            }
            return [...prev, userId];
        });
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
    }, []);

    const handleSave = useCallback(async () => {
        if (usersToRemove.length > 0) {
            try {
                setSaving(true);
                setError(null);
                await userGroupsService.detachUsers(groupId, usersToRemove);
                onClose();
            } catch (error) {
                setError(t('userGroups.error.removing'));
                console.error('Failed to remove users:', error);
                fetchUsers();
            } finally {
                setSaving(false);
            }
        } else {
            onClose();
        }
    }, [groupId, usersToRemove, onClose, t, fetchUsers]);

    return {
        users,
        loading,
        saving,
        error,
        modalRef,
        usersToRemove,
        handleRemoveUser,
        handleSave,
    };
}; 